import { computed, inject, Injectable, signal } from '@angular/core';
import { AuthApiService } from '@pressteaze/auth';
import type {
	OperatorContext,
	OperatorDashboard,
	OperatorReservation,
	OperatorReservationInput,
	OperatorReservationUpdate,
	OperatorTable,
	ReservationStatus,
} from '@pressteaze/domain';
import { MessagesService } from '@pressteaze/ui';
import { OperatorApiError, OperatorApiService } from '../api-services/operator-api.service';

const REALTIME_NOTICE_DELAY_MS = 8_000;
const SHIFT_STATUS_ORDER: Readonly<Record<ReservationStatus, number>> = {
	booked: 0,
	arrived: 1,
	pending: 2,
	cancelled: 3,
	no_show: 3,
	expired: 3,
	completed: 4,
};

@Injectable({ providedIn: 'root' })
export class OperatorStateService {
	private readonly operatorApiSrv = inject(OperatorApiService);
	private readonly authApiSrv = inject(AuthApiService);
	private readonly messagesSrv = inject(MessagesService);
	private disconnectRealtime?: () => Promise<void>;
	private realtimeTransition: Promise<void> = Promise.resolve();
	private realtimeConnectionId = 0;
	private realtimeWasConnected = false;
	private realtimeNoticeShown = false;
	private realtimeNoticeTimer?: ReturnType<typeof setTimeout>;
	private loadSequence = 0;
	private activeView: 'dashboard' | 'shift' = 'shift';

	readonly context = signal<OperatorContext | null>(null);
	readonly dashboard = signal<OperatorDashboard | null>(null);
	readonly reservations = signal<readonly OperatorReservation[]>([]);
	readonly realtime = signal(false);
	readonly serviceDate = signal('');
	readonly search = signal('');
	readonly loading = signal(true);
	readonly loadFailed = signal(false);
	readonly saving = signal(false);
	readonly shiftReservations = computed(() =>
		[...this.reservations()].sort((left, right) => {
			const statusDifference =
				SHIFT_STATUS_ORDER[left.status] - SHIFT_STATUS_ORDER[right.status];
			if (statusDifference !== 0) return statusDifference;
			const timeDifference = left.startsAt.localeCompare(right.startsAt);
			return timeDifference || left.id.localeCompare(right.id);
		}),
	);
	readonly activeCount = computed(
		() =>
			this.reservations().filter(
				(item) =>
					!['completed', 'cancelled', 'no_show', 'expired'].includes(item.status),
			).length,
	);
	readonly pendingCount = computed(
		() =>
			this.reservations().filter((item) => item.status === 'pending').length,
	);
	readonly arrivedCount = computed(
		() =>
			this.reservations().filter((item) => item.status === 'arrived').length,
	);

	async initialize(): Promise<void> {
		await this.initializeContext();
		if (this.context()) await this.activateShift();
	}

	async initializeContext(): Promise<void> {
		this.loading.set(true);
		this.loadFailed.set(false);
		try {
			const context = this.context() ?? (await this.operatorApiSrv.getContext());
			if (!this.context()) this.context.set(context);
			if (!this.serviceDate())
				this.serviceDate.set(this.localToday(context.timezone));
		} catch {
			this.loadFailed.set(true);
			this.messagesSrv.error(
				'Δεν φορτώθηκε το κατάστημα',
				'Ελέγξτε τη σύνδεση και δοκιμάστε ξανά.',
			);
		} finally {
			this.loading.set(false);
		}
	}

	async activateDashboard(): Promise<void> {
		this.activeView = 'dashboard';
		if (!this.context()) return;
		await this.loadDashboard();
		await this.startRealtime();
	}

	async activateShift(): Promise<void> {
		this.activeView = 'shift';
		if (!this.context()) return;
		await this.loadDay();
		await this.startRealtime();
	}

	async loadDashboard(): Promise<void> {
		const context = this.context();
		if (!context) return;
		const sequence = ++this.loadSequence;
		this.loading.set(true);
		try {
			const today = this.localToday(context.timezone);
			const dashboard = await this.operatorApiSrv.getDashboard(
				today,
				this.addDays(today, -7),
				today,
			);
			if (sequence === this.loadSequence) this.dashboard.set(dashboard);
		} catch {
			this.messagesSrv.error(
				'Δεν φορτώθηκε η αρχική οθόνη',
				'Δοκιμάστε ξανά σε λίγο.',
			);
		} finally {
			if (sequence === this.loadSequence) this.loading.set(false);
		}
	}

	async loadDay(): Promise<void> {
		if (!this.serviceDate()) return;
		const sequence = ++this.loadSequence;
		this.loading.set(true);
		try {
			const reservations = await this.operatorApiSrv.getDay(
				this.serviceDate(),
				this.search(),
			);
			if (sequence === this.loadSequence) this.reservations.set(reservations);
		} catch {
			this.messagesSrv.error(
				'Δεν φορτώθηκαν οι κρατήσεις',
				'Δοκιμάστε ξανά σε λίγο.',
			);
		} finally {
			if (sequence === this.loadSequence) this.loading.set(false);
		}
	}

	async create(input: OperatorReservationInput): Promise<boolean> {
		return this.save(
			() => this.operatorApiSrv.createReservation(input),
			input.source === 'walk_in'
				? 'Το walk-in καταχωρίστηκε'
				: 'Η τηλεφωνική κράτηση καταχωρήθηκε',
		);
	}

	async getAvailableTables(
		partySize: number,
		startsLocal: string,
		areaId: string | null,
		excludeReservationId: string | null,
	): Promise<readonly OperatorTable[]> {
		const context = this.context();
		if (!context) return [];
		const tableIds = await this.operatorApiSrv.getAvailableTableIds(
			partySize,
			startsLocal,
			areaId,
			excludeReservationId,
		);
		const tablesById = new Map(
			context.tables.map((table) => [table.id, table]),
		);
		return tableIds.flatMap((id) => {
			const table = tablesById.get(id);
			return table ? [table] : [];
		});
	}

	async getTimeOptions(localDate: string): Promise<readonly string[]> {
		if (!this.context()) return [];
		return this.operatorApiSrv.getTimeOptions(localDate);
	}

	async update(input: OperatorReservationUpdate): Promise<boolean> {
		return this.save(
			() => this.operatorApiSrv.updateReservation(input),
			'Η κράτηση ενημερώθηκε',
		);
	}

	async transition(reservation: OperatorReservation, status: ReservationStatus): Promise<void> {
		await this.save(
			() =>
				this.operatorApiSrv.transitionReservation(
					reservation.id,
					reservation.version,
					status,
				),
			'Η κατάσταση ενημερώθηκε',
		);
	}

	async switchStore(storeId: string): Promise<void> {
		if (storeId === this.context()?.storeId)
			return;

		this.loading.set(true);

		try {
			await this.stopRealtime();
			await this.authApiSrv.switchStore(storeId);
			this.context.set(null);
			this.dashboard.set(null);
			this.reservations.set([]);
			this.serviceDate.set('');
			await this.initializeContext();
			await this.activateCurrentView();
			this.messagesSrv.success('Το ενεργό κατάστημα άλλαξε');
		} catch {
			this.messagesSrv.error(
				'Δεν άλλαξε το κατάστημα',
				'Η τρέχουσα βάρδια παραμένει ενεργή.',
			);
		} finally {
			this.loading.set(false);
		}
	}

	async logout(): Promise<void> {
		await Promise.allSettled([
			this.stopRealtime(),
			this.authApiSrv.logoutOperator(),
		]);
		this.loadSequence += 1;
		this.context.set(null);
		this.dashboard.set(null);
		this.reservations.set([]);
		this.serviceDate.set('');
		this.search.set('');
	}

	async destroy(): Promise<void> {
		await this.stopRealtime();
	}

	private async save(action: () => Promise<void>, successMessage: string): Promise<boolean> {
		if (this.saving())
			return false;

		this.saving.set(true);

		try {
			await action();
			this.messagesSrv.success(successMessage);
			await this.reloadCurrentView();
			return true;
		} catch (error) {
			this.showMutationError(error);
			if (
				error instanceof OperatorApiError &&
				(
					[
						'VERSION_CONFLICT',
						'NO_AVAILABILITY',
						'CANCELLED_RESERVATION_NOT_EDITABLE',
						'EXPIRED_RESERVATION_NOT_EDITABLE',
					] as string[]
				).includes(error.code)
			) {
				await this.reloadCurrentView();
			}
			return false;
		} finally {
			this.saving.set(false);
		}
	}

	private async startRealtime(): Promise<void> {
		const context = this.context();
		if (!context)
			return;

		this.realtime.set(false);

		await this.queueRealtimeTransition(async () => {
			await this.disconnectRealtimeNow();
			const connectionId = ++this.realtimeConnectionId;

			this.disconnectRealtime = await this.operatorApiSrv.connectRealtime(
				context.storeId,
				() => void this.reloadCurrentView(),
				(connected) => this.updateRealtimeStatus(connectionId, connected),
			);
		});
	}

	private async activateCurrentView(): Promise<void> {
		if (this.activeView === 'dashboard') await this.activateDashboard();
		else await this.activateShift();
	}

	private async reloadCurrentView(): Promise<void> {
		if (this.activeView === 'dashboard') await this.loadDashboard();
		else await this.loadDay();
	}

	private async stopRealtime(): Promise<void> {
		await this.queueRealtimeTransition(() => this.disconnectRealtimeNow());
	}

	private async disconnectRealtimeNow(): Promise<void> {
		this.realtimeConnectionId += 1;
		this.realtime.set(false);
		this.realtimeWasConnected = false;
		this.realtimeNoticeShown = false;
		this.clearRealtimeNoticeTimer();

		const disconnect = this.disconnectRealtime;
		this.disconnectRealtime = undefined;

		if (disconnect)
			await disconnect();
	}

	private updateRealtimeStatus(connectionId: number, connected: boolean): void {
		if (connectionId !== this.realtimeConnectionId)
			return;

		const reconnected = connected && this.realtimeWasConnected && !this.realtime();
		this.realtime.set(connected);

		if (connected) {
			this.realtimeWasConnected = true;
			this.realtimeNoticeShown = false;
			this.clearRealtimeNoticeTimer();
			if (reconnected)
				void this.reloadCurrentView();
			return;
		}

		if (
			!this.realtimeWasConnected ||
			this.realtimeNoticeShown ||
			this.realtimeNoticeTimer
		)
			return;

		this.realtimeNoticeTimer = setTimeout(() => {
			this.realtimeNoticeTimer = undefined;
			if (connectionId !== this.realtimeConnectionId || this.realtime())
				return;

			this.realtimeNoticeShown = true;
			this.messagesSrv.info(
				'Ο ζωντανός συγχρονισμός έχει διακοπεί',
				'Γίνεται προσπάθεια επανασύνδεσης. Οι κρατήσεις θα συγχρονιστούν μόλις αποκατασταθεί η σύνδεση.',
			);
		}, REALTIME_NOTICE_DELAY_MS);
	}

	private clearRealtimeNoticeTimer(): void {
		if (!this.realtimeNoticeTimer)
			return;
		clearTimeout(this.realtimeNoticeTimer);
		this.realtimeNoticeTimer = undefined;
	}

	private queueRealtimeTransition(action: () => Promise<void>): Promise<void> {
		const transition = this.realtimeTransition
			.catch(() => undefined)
			.then(action);
		this.realtimeTransition = transition;
		return transition;
	}

	private showMutationError(error: unknown): void {
		const code = error instanceof OperatorApiError ? error.code : '';
		if (code === 'VERSION_CONFLICT') {
			this.messagesSrv.info(
				'Η κράτηση άλλαξε από άλλη συσκευή',
				'Φορτώσαμε την πιο πρόσφατη έκδοση.',
			);
		} else if (code === 'NO_AVAILABILITY') {
			this.messagesSrv.error(
				'Το τραπέζι δεν είναι διαθέσιμο',
				'Επιλέξτε άλλη ώρα ή άλλο τραπέζι.',
			);
		} else if (code === 'CANCELLED_RESERVATION_NOT_EDITABLE') {
			this.messagesSrv.info(
				'Η ακυρωμένη κράτηση δεν αλλάζει',
				'Φορτώσαμε την πιο πρόσφατη έκδοση.',
			);
		} else if (code === 'EXPIRED_RESERVATION_NOT_EDITABLE') {
			this.messagesSrv.info(
				'Η κράτηση έληξε χωρίς επιβεβαίωση',
				'Φορτώσαμε την πιο πρόσφατη έκδοση.',
			);
		} else if (code === 'INVALID_BOOKING_SLOT') {
			this.messagesSrv.info(
				'Η κράτηση είναι εκτός ωραρίου',
				'Η επιλεγμένη ώρα δεν βρίσκεται στο ωράριο λειτουργίας του καταστήματος.',
			);
		} else if (code === 'INVALID_REQUEST') {
			this.messagesSrv.error(
				'Ελέγξτε τα στοιχεία της κράτησης',
				'Κάποιο πεδίο δεν είναι έγκυρο.',
			);
		} else {
			this.messagesSrv.error(
				'Η ενέργεια δεν ολοκληρώθηκε',
				'Δοκιμάστε ξανά σε λίγο.',
			);
		}
	}

	private localToday(timezone: string): string {
		return new Intl.DateTimeFormat('en-CA', {
			timeZone: timezone,
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
		}).format(new Date());
	}

	private addDays(value: string, days: number): string {
		const [year, month, day] = value.split('-').map(Number);
		const date = new Date(Date.UTC(year, month - 1, day));
		date.setUTCDate(date.getUTCDate() + days);
		return date.toISOString().slice(0, 10);
	}
}
